import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Cpu, CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { useBookingConfig } from '@/lib/BookingConfigContext';
import { useLiveRigStatuses } from '@/hooks/useLiveRigStatuses';
import RigStatusBadge from './RigStatusBadge';

// Customer-facing rig picker. Only renders rigs that are in the selected
// experience's allowed_rigs array — inappropriate rigs are hidden entirely.
export default function BookingStepRigPicker({ rigType, onSelect, allowedRigs = [], rigNextAvailable = {} }) {
  const { rigs } = useBookingConfig();
  const [specsRig, setSpecsRig] = React.useState(null);
  const statusFor = useLiveRigStatuses();

  const allowedSet = new Set(allowedRigs);

  const list = Object.entries(rigs)
    .map(([key, rig]) => ({ key, ...rig }))
    .filter((r) => r.active !== false && allowedSet.has(r.key))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  return (
    <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
      <p className="text-white/40 text-sm mb-6">
        {list.length === 1
          ? 'Your session is assigned to the rig below. Continue to pick a date and time.'
          : 'Choose your preferred rig for your session.'}
      </p>
      {list.length === 0 ? (
        <div className="border border-dashed border-white/10 p-10 text-center">
          <p className="text-white/30 text-sm">No rigs are configured for this experience yet. Please contact us to book.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {list.map(({ key, ...rig }, i) => {
            const selected = rigType === key;
            const hasSpecs = Array.isArray(rig.specs) && rig.specs.filter(Boolean).length > 0;
            const feed = statusFor({ key, ...rig });
            const nextAvail = rigNextAvailable[key];
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`w-full text-left p-6 transition-all duration-300 relative overflow-hidden border ${
                  selected ? 'bg-primary/10 border-primary/40' : 'bg-primary/5 border-primary/30 hover:border-primary hover:bg-primary/10'
                }`}
              >
                {selected && <div className="absolute top-0 left-0 right-0 h-[2px] bg-primary" />}

                <button type="button" onClick={() => onSelect(key)} className="w-full text-left">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 pr-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold tracking-widest text-primary">{rig.tag}</span>
                        {feed && <RigStatusBadge status={feed.status} />}
                      </div>
                      <h3 className="font-black text-sm uppercase leading-tight text-white">{rig.name}</h3>
                    </div>
                    <div className={`w-5 h-5 border-2 flex items-center justify-center shrink-0 mt-1 transition-all ${
                      selected ? 'border-primary bg-primary' : 'border-white/20'
                    }`}>
                      {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </div>
                  </div>

                  <p className="text-xs leading-relaxed text-white/40">{rig.description}</p>
                </button>

                {/* Next available date chip */}
                {nextAvail !== undefined && (
                  <div className="mt-3 flex items-center gap-1.5 text-[10px]">
                    {nextAvail ? (
                      <>
                        <CalendarDays className="w-3 h-3 text-emerald-500" />
                        <span className="text-white/40">Next available: <span className="text-white/70 font-bold">{format(new Date(nextAvail.date + 'T00:00:00'), 'd MMM')}</span></span>
                      </>
                    ) : (
                      <span className="text-red-400/70 font-bold tracking-widest">BOOKED OUT</span>
                    )}
                  </div>
                )}

                {hasSpecs && (
                  <button
                    type="button"
                    onClick={() => setSpecsRig({ key, ...rig })}
                    className="mt-4 flex items-center gap-1.5 text-primary text-[11px] font-bold tracking-wider hover:text-primary/80 transition-colors"
                  >
                    <Cpu className="w-3.5 h-3.5" /> VIEW FULL SPECS
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {specsRig && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSpecsRig(null)}
            className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.94, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 16 }}
              transition={{ type: 'spring', stiffness: 240, damping: 22 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg bg-[#111115] border border-white/10 overflow-hidden"
            >
              <button
                onClick={() => setSpecsRig(null)}
                className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white/60 hover:text-white transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>

              {specsRig.image_url && (
                <div className="w-full aspect-[16/10] bg-black overflow-hidden">
                  <img src={specsRig.image_url} alt={specsRig.name} className="w-full h-full object-cover" />
                </div>
              )}

              <div className="p-7">
              <span className="text-primary text-[10px] font-bold tracking-widest block mb-1">{specsRig.tag}</span>
              <h3 className="text-white font-black text-xl uppercase mb-1 pr-8">{specsRig.name}</h3>
              <p className="text-white/40 text-xs mb-5">Technical build specifications</p>

              <div className="space-y-2.5 max-h-[60vh] overflow-y-auto pr-1">
                {specsRig.specs.filter(Boolean).map((spec, idx) => (
                  <div key={idx} className="flex items-start gap-2.5 text-sm text-white/70 border border-white/5 bg-white/[0.02] px-3.5 py-2.5">
                    <Check className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" strokeWidth={3} />
                    <span>{spec}</span>
                  </div>
                ))}
              </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}